/* eslint-disable max-len */
import EaFeaturedIcon1 from '@propertypal/shared/src/resources/icons/premier-listing/ea-featured-icon1.svg';
import EaFeaturedIcon2 from '@propertypal/shared/src/resources/icons/premier-listing/ea-featured-icon2.svg';
import EaFeaturedIcon3 from '@propertypal/shared/src/resources/icons/premier-listing/ea-featured-icon3.svg';
import PnFeaturedIcon1 from '@propertypal/shared/src/resources/icons/premier-listing/pn-premier-icon1.svg';
import PnFeaturedIcon2 from '@propertypal/shared/src/resources/icons/premier-listing/pn-premier-icon2.svg';
import PnFeaturedIcon3 from '@propertypal/shared/src/resources/icons/premier-listing/pn-premier-icon3.svg';
import PnPremierMobMockup from '@propertypal/shared/src/resources/images/promote/pn-premier-mob-mockup.png';
import PnPremierMockup from '@propertypal/shared/src/resources/images/promote/pn-premier-mockup.png';
import PremierFeatureMockup from '@propertypal/shared/src/resources/images/promote/premier-iPhone.png';
import PremierMockup from '@propertypal/shared/src/resources/images/promote/premier-macbook.png';
import dayjs, { parseApiDate, getDaysBetweenDates } from '@propertypal/shared/src/services/dayjs';
import { Property } from '@propertypal/shared/src/types';
import platform from '@propertypal/shared/src/utils/platform';
import PromoteFeatures, { PromoteFeaturesIcon } from '@propertypal/web-ui/src/promote/PromoteFeatures';
import PromoteTable from '@propertypal/web-ui/src/promote/PromoteTable';
import React, { useMemo } from 'react';
import PromoteHeader from '../headers/promote/PromoteHeader';
import { TextLink } from '../typography';
import { BookButton, CanBook } from './PremierBookButton';
import PromoteSuperfeature from './PromoteSuperfeature';

const icons: PromoteFeaturesIcon[] = [
  {
    SVG: platform.select(EaFeaturedIcon2, PnFeaturedIcon2),
    heading: '4 times',
    text: 'boost the property above a standard advert',
    maxW: 250,
  },
  {
    SVG: platform.select(EaFeaturedIcon1, PnFeaturedIcon1),
    heading: 'Enhanced',
    text: 'with additional photographs for the lifetime of the advert',
    maxW: 270,
  },
  {
    SVG: platform.select(EaFeaturedIcon3, PnFeaturedIcon3),
    heading: '3.5 times',
    text: 'more enquiries than a standard listing',
    maxW: 200,
  },
];

interface PremierListingContentProps {
  products?: boolean;
  property?: Property | null;
}

const PremierListingContent: React.FC<PremierListingContentProps> = ({ products, property }) => {
  const { canBook, button }: { canBook: CanBook; button: BookButton } = useMemo(() => {
    const premierBoostAvailableFrom = property?.premierBoostAvailableFrom
      ? parseApiDate(property.premierBoostAvailableFrom)
      : null;

    // only show date available to boost, if the boost is available from is in the future
    const showDate = premierBoostAvailableFrom
      ? getDaysBetweenDates(dayjs(), premierBoostAvailableFrom).length > 1
      : null;

    return {
      canBook: {
        available: !property ? true : property?.premierAvailable || false,
        availableTimePeriod:
          premierBoostAvailableFrom && showDate ? premierBoostAvailableFrom.format('Do MMMM YYYY') : null,
        title: `This property is already a Premier Listing.`,
        subtitle: property?.premierBoostAvailableFrom ? (
          <>
            You can purchase a{' '}
            <TextLink prefetch={false} href={`/promote/boost?propertyId=${property.pathId}`}>
              Premier Boost
            </TextLink>
            {showDate && <> from</>}
          </>
        ) : undefined,
      },
      button: {
        href: `${platform.ordersUrl}/premier-property${property ? `/${property.id}` : ''}`,
        text: 'Book a Premier Listing',
      },
    };
  }, [property]);

  return (
    <>
      <PromoteHeader products={products} propertyId={property?.pathId} />
      <PromoteSuperfeature
        title="Premier Listing"
        mockup={{
          src: platform.select(PremierMockup.src, PnPremierMockup.src),
          alt: 'Premier Listing',
          aspectRatio: '1430/863',
        }}
        badge={{
          title: 'Only £99',
          text: 'for the lifetime of your listing',
          subtext: '*£49 for a rental property',
        }}
        button={button}
        features={[
          [
            {
              title: 'Extra Photos',
              text: 'Highlight your property with a larger main image and additional photos of your choice.',
            },
          ],
          [
            { title: 'Larger Listing', text: 'Get double the size of a standard listing for increased visibility.' },
            {
              title: 'More Exposure',
              text: 'Get your property to the top of search results, attracting more potential buyers.',
            },
          ],
        ]}
        canBook={canBook}
        pb={!canBook?.available ? 60 : undefined}
      />
      <PromoteFeatures
        icons={icons}
        mockup={{
          src: platform.select(PremierFeatureMockup.src, PnPremierMobMockup.src),
          alt: 'Premier Listing',
          maxW: 295,
        }}
        featureTitle="Premier Listing Advantages:"
        features={[
          `Stand out with a larger, more prominent listing that includes a bigger photograph, putting your property front and centre.`,
          `Feature up to 4 photos directly on the search results page, highlighting your property's best features.`,
          `Boost your listing to the top of the search results on 4 occasions giving your property more visibility.`,
          `Unlimited exposure - your advert stays live until your property is sold, with no expiry or time limits.`,
          `Maximum reach on the two most popular property portals in Northern Ireland, ensuring your property is seen by the largest possible audience.`,
        ]}
        button={button}
        canBook={canBook?.available}
      />
      <PromoteTable highlightIndex={1} property={property} />
    </>
  );
};

export default PremierListingContent;
